import React, { useEffect, useState } from 'react';
import { Input, Select } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Card } from '../../../components/CssFrameworkComponents';
import { CustomTooltip } from '../../../components/Tooltip';
import SaveIndicator from '../components/SaveIndicator';
import SettingsToggleRow from '../components/SettingsToggleRow';
import { useAutoSaveSetting } from '../hooks/useAutoSaveSetting';
import { useOptimisticChecked } from '../hooks/useOptimisticChecked';
import { useIsDisabledForRole } from '../../../components/ButtonWithRole';
import { usePrerenderUser } from '../../../hooks/usePrerenderUser';
import { defaultDesktopUserAgent, defaultMobileUserAgent } from '../redux/settingsReducer';
import { withSelectedOption } from '../selectOption';
import { SETTINGS_RESTRICTED_ROLES } from '../settingsRoles';

const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string

export const USER_AGENT_POSTFIX = ' Prerender (+https://github.com/prerender/prerender)';
const postfixRegexp = new RegExp(`${escapeRegExp(USER_AGENT_POSTFIX)}$`);

export const USER_AGENT_DEBOUNCE_MS = 800;
const CUSTOM_RESOLUTION_TEXT = 'Custom';

const RESOLUTION_LABEL = 'Screen Resolution';
const USER_AGENT_LABEL = 'User Agent';
const USER_AGENT_TOOLTIP =
  'Prerender automatically appends (+https://github.com/prerender/prerender) to the user agent.';

const withSelectedResolution = (options, viewport) =>
  withSelectedOption(
    options,
    (option) => option.width === viewport.width && option.height === viewport.height,
    () => ({
      text: `${CUSTOM_RESOLUTION_TEXT} (${viewport.width}x${viewport.height})`,
      width: viewport.width,
      height: viewport.height,
    })
  );

const normalizeUserAgent = (value, defaultUserAgent) => {
  if (value === '' || value === defaultUserAgent) return defaultUserAgent;
  return value.replace(postfixRegexp, '') + USER_AGENT_POSTFIX;
};

const RenderingDeviceCard = ({ device, title, icon, resolutionOptions }) => {
  const { settings, mobileAdaptive } = usePrerenderUser();
  // Two independent auto-savers: the rendering fields (resolution + user agent) and the device
  // status toggle. Keeping them apart means a failed user-agent save cannot revert the toggle's
  // optimistic state, and each save indicator/retry stays scoped to the field it belongs to.
  // Concurrent partial writes are safe: the API merges the settings patch in SQL.
  const renderingSave = useAutoSaveSetting();
  const deviceStatusSave = useAutoSaveSetting();
  const disabledForRole = useIsDisabledForRole(SETTINGS_RESTRICTED_ROLES);

  const isDesktop = device === 'desktop';
  const viewport = isDesktop ? settings.customViewport : settings.customMobileViewport;
  const serverUserAgent = isDesktop ? settings.customDesktopUserAgent : settings.customMobileUserAgent;
  const defaultUserAgent = isDesktop ? defaultDesktopUserAgent : defaultMobileUserAgent;
  const viewportField = isDesktop ? 'customViewport' : 'customMobileViewport';
  const userAgentField = isDesktop ? 'customDesktopUserAgent' : 'customMobileUserAgent';

  const [draftUserAgent, setDraftUserAgent] = useState(serverUserAgent);
  const { checked: mobileChecked, setPendingChecked: setMobilePending } = useOptimisticChecked(
    mobileAdaptive,
    deviceStatusSave.status
  );

  useEffect(() => {
    setDraftUserAgent(serverUserAgent);
  }, [serverUserAgent]);

  const options = withSelectedResolution(resolutionOptions, viewport);
  const currentResolutionIndex = options.findIndex((option) => option.selected);
  const renderingControlsDisabled = disabledForRole || renderingSave.status === 'saving';
  const toggleDisabled = disabledForRole || deviceStatusSave.status === 'saving';
  const resolutionId = `resolution-${device}`;
  const userAgentId = `userAgent-${device}`;

  const onResolutionChanged = (index) => {
    const current = options[index];
    renderingSave.save({ [viewportField]: { width: current.width, height: current.height } });
  };

  const onUserAgentChanged = (event) => {
    setDraftUserAgent(event.target.value);
  };

  const onUserAgentLostFocus = () => {
    const normalized = normalizeUserAgent(draftUserAgent, defaultUserAgent);
    setDraftUserAgent(normalized);

    // Reverting a scheduled change back to the stored value drops the pending save instead of
    // leaving it to fire the stale value.
    if (normalized === serverUserAgent) {
      renderingSave.cancelPending();
      return;
    }

    renderingSave.saveDebounced({ [userAgentField]: normalized }, USER_AGENT_DEBOUNCE_MS);
  };

  return (
    <Card title={title}>
      <div className="row">
        <div className="col-2 align-self-center">
          <div className="row">
            <i className={`fa-3x fe ${icon}`}></i>
          </div>
        </div>
        <div className="col">
          <div className="row align-items-center pb-3">
            <div className="col-3">
              <label htmlFor={resolutionId}>{RESOLUTION_LABEL}</label>
            </div>
            <div className="col">
              <Select
                name={resolutionId}
                id={resolutionId}
                onChange={onResolutionChanged}
                // Controlled by the stored viewport, not the last click: a failed save leaves the
                // server state untouched, so the select snaps back to what is actually persisted
                // instead of advertising a resolution that never landed. A successful save moves it
                // forward via the getPrerenderUser refresh the mutation dispatches.
                value={currentResolutionIndex}
                disabled={renderingControlsDisabled}
                popupMatchSelectWidth={false}
                style={{ width: 220 }}
              >
                {options.map((option, i) => (
                  <Select.Option key={i} value={i}>
                    {option.text}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          <div className="row align-items-center pb-3">
            <div className="col-3">
              <label htmlFor={userAgentId}>{USER_AGENT_LABEL}</label>{' '}
              <CustomTooltip placement="top" message={USER_AGENT_TOOLTIP}>
                <InfoCircleOutlined />
              </CustomTooltip>
            </div>
            <div className="col">
              {/* Multi-line so the whole user agent is readable at a glance instead of being
                  truncated in a single-line field (the postfix appended on blur pushes the string
                  well past one line). autoSize grows to fit the value and caps the height so an
                  unusually long agent can't blow up the card layout. */}
              <Input.TextArea
                name={userAgentId}
                id={userAgentId}
                onChange={onUserAgentChanged}
                onBlur={onUserAgentLostFocus}
                value={draftUserAgent}
                disabled={renderingControlsDisabled}
                autoSize={{ minRows: 2, maxRows: 6 }}
              />
            </div>
            <div className="col-auto align-self-center">
              <SaveIndicator status={renderingSave.status} onRetry={renderingSave.retry} />
            </div>
          </div>
          {isDesktop ? (
            <SettingsToggleRow
              checked
              disabled
              ariaLabel={`${title} status`}
              status={deviceStatusSave.status}
              onRetry={deviceStatusSave.retry}
            />
          ) : (
            <SettingsToggleRow
              checked={mobileChecked}
              onChange={(checked) => {
                setMobilePending(checked);
                deviceStatusSave.save({ mobileAdaptive: checked });
              }}
              disabled={toggleDisabled}
              disabledFor={SETTINGS_RESTRICTED_ROLES}
              ariaLabel={`${title} status`}
              status={deviceStatusSave.status}
              onRetry={deviceStatusSave.retry}
            />
          )}
        </div>
      </div>
    </Card>
  );
};

export default RenderingDeviceCard;
